import { captureForms, conversionStats, sourceBreakdown, formResponses } from '../../../mocks/leadCapture';

export default function ConversionAnalytics() {
  const maxViews = Math.max(...conversionStats.map((s) => s.views));
  const maxSubs = Math.max(...conversionStats.map((s) => s.submissions));

  const formStats = captureForms.map((f) => {
    const responses = formResponses.filter((r) => r.formId === f.id);
    const converted = responses.filter((r) => r.status === 'Converted').length;
    const rate = responses.length > 0 ? Math.round((converted / responses.length) * 100) : 0;
    const viewRate = f.views > 0 ? Math.round((f.submissions / f.views) * 100) : 0;
    return { ...f, convRate: rate, viewRate, responseCount: responses.length };
  }).sort((a, b) => b.submissions - a.submissions);

  const totalSource = sourceBreakdown.reduce((acc, s) => acc + s.count, 0);

  const funnelSteps = [
    { label: 'Page Views', value: 4110, color: '#0ea5e9' },
    { label: 'Form Opens', value: 2180, color: '#6366f1' },
    { label: 'Form Starts', value: 1050, color: '#f59e0b' },
    { label: 'Submissions', value: 601, color: '#10b981' },
    { label: 'Converted Leads', value: 244, color: '#f43f5e' },
  ];
  const maxFunnel = funnelSteps[0].value;

  return (
    <div className="space-y-6">
      {/* Funnel + Bar chart side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Conversion Funnel */}
        <div className="bg-white border border-gray-100 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-gray-800 mb-1">Conversion Funnel</h3>
          <p className="text-xs text-gray-400 mb-5">All-time aggregate across all forms</p>
          <div className="space-y-3">
            {funnelSteps.map((step, i) => {
              const pct = Math.round((step.value / maxFunnel) * 100);
              const dropoff = i > 0 ? Math.round(((funnelSteps[i - 1].value - step.value) / funnelSteps[i - 1].value) * 100) : null;
              return (
                <div key={step.label}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-gray-700">{step.label}</span>
                      {dropoff !== null && (
                        <span className="text-xs text-gray-400 bg-gray-50 px-1.5 py-0.5 rounded">-{dropoff}%</span>
                      )}
                    </div>
                    <span className="text-sm font-bold text-gray-800">{step.value.toLocaleString()}</span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{ width: `${pct}%`, backgroundColor: step.color }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Monthly trend bars */}
        <div className="bg-white border border-gray-100 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-gray-800 mb-1">Monthly Submissions vs Views</h3>
          <p className="text-xs text-gray-400 mb-4">Last 6 months</p>
          <div className="flex items-end gap-2 h-40">
            {conversionStats.map((stat) => (
              <div key={stat.month} className="flex-1 flex flex-col items-center gap-1">
                <div className="w-full flex items-end gap-0.5 h-32">
                  {/* Views bar */}
                  <div
                    className="flex-1 rounded-t-sm bg-sky-100"
                    style={{ height: `${Math.round((stat.views / maxViews) * 100)}%` }}
                    title={`Views: ${stat.views}`}
                  />
                  {/* Submissions bar */}
                  <div
                    className="flex-1 rounded-t-sm bg-emerald-400"
                    style={{ height: `${Math.round((stat.submissions / maxViews) * 100)}%` }}
                    title={`Submissions: ${stat.submissions}`}
                  />
                </div>
                <span className="text-xs text-gray-400 text-center">{stat.month}</span>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-4 mt-3">
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <div className="w-3 h-3 rounded-sm bg-sky-100" /> Views
            </div>
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <div className="w-3 h-3 rounded-sm bg-emerald-400" /> Submissions
            </div>
          </div>
        </div>
      </div>

      {/* Form Performance Table */}
      <div className="bg-white border border-gray-100 rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-800">Form Performance</h3>
          <p className="text-xs text-gray-400 mt-0.5">Click-through and conversion rates per form</p>
        </div>
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-100">
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Form</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Views</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Submissions</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Sub. Rate</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Converted</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Conv. Rate</th>
              <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
            </tr>
          </thead>
          <tbody>
            {formStats.map((f) => (
              <tr key={f.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                <td className="px-5 py-3">
                  <div className="flex items-center gap-2.5">
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${f.color}18` }}>
                      <i className={`${f.icon} text-sm`} style={{ color: f.color }} />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-800">{f.name}</p>
                      <p className="text-xs text-gray-400">{f.category}</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-right text-sm text-gray-700">{f.views.toLocaleString()}</td>
                <td className="px-4 py-3 text-right text-sm font-medium text-gray-800">{f.submissions}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${Math.min(f.viewRate * 3, 100)}%`, backgroundColor: f.color }} />
                    </div>
                    <span className="text-xs text-gray-600 font-medium">{f.viewRate}%</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-right text-sm font-semibold" style={{ color: '#10b981' }}>{f.converted}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${Math.min(f.convRate * 1.5, 100)}%`,
                          backgroundColor: f.convRate >= 40 ? '#10b981' : f.convRate >= 20 ? '#f59e0b' : '#94a3b8',
                        }}
                      />
                    </div>
                    <span className={`text-xs font-semibold ${f.convRate >= 40 ? 'text-emerald-600' : f.convRate >= 20 ? 'text-amber-600' : 'text-gray-500'}`}>{f.convRate}%</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-center">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${f.status === 'Active' ? 'bg-emerald-50 text-emerald-700' : f.status === 'Draft' ? 'bg-amber-50 text-amber-700' : 'bg-gray-100 text-gray-500'}`}>
                    {f.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Traffic Source Breakdown */}
      <div className="bg-white border border-gray-100 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-gray-800 mb-1">Traffic Source Breakdown</h3>
        <p className="text-xs text-gray-400 mb-5">Where your form submissions come from</p>
        <div className="space-y-3">
          {sourceBreakdown.map((s) => {
            const pct = Math.round((s.count / totalSource) * 100);
            return (
              <div key={s.source} className="flex items-center gap-3">
                <span className="text-sm text-gray-700 w-40 flex-shrink-0">{s.source}</span>
                <div className="flex-1 bg-gray-100 rounded-full h-2.5 overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: s.color }} />
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-sm font-semibold text-gray-800 w-8 text-right">{pct}%</span>
                  <span className="text-xs text-gray-400 w-14 text-right">{s.count.toLocaleString()} subs</span>
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-5 pt-4 border-t border-gray-100 grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Total Submissions', value: '601', icon: 'ri-send-plane-line', color: '#0ea5e9' },
            { label: 'Best Performing', value: 'Visa Form', icon: 'ri-trophy-line', color: '#f59e0b' },
            { label: 'Top Source', value: 'Direct / Website', icon: 'ri-global-line', color: '#10b981' },
            { label: 'Avg Conv. Rate', value: '38.4%', icon: 'ri-percent-line', color: '#6366f1' },
          ].map((item) => (
            <div key={item.label} className="p-3 bg-gray-50 rounded-xl">
              <div className="w-7 h-7 flex items-center justify-center mb-2" style={{ color: item.color }}>
                <i className={`${item.icon} text-lg`} />
              </div>
              <p className="text-sm font-bold text-gray-800">{item.value}</p>
              <p className="text-xs text-gray-400 mt-0.5">{item.label}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
